import type {
  ElementBounds,
  Point,
} from "../components/MainVidPlayer/TransformHandles/types";

/**
 * Escape a string for use in CSS selectors.
 * Handles special characters like $, which are common in bl_edit_selector classes.
 */
function escapeCssSelector(str: string): string {
  // Use CSS.escape if available, otherwise manually escape
  if (typeof CSS !== "undefined" && CSS.escape) {
    return CSS.escape(str);
  }
  // Manual fallback for older browsers
  return str.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
}

/**
 * Extract the rotation angle from a CSS transform matrix.
 * Returns rotation in degrees.
 */
function extractRotationFromMatrix(matrix: DOMMatrix): number {
  // For a 2D rotation matrix:
  // a = cos(angle), b = sin(angle)
  // So angle = atan2(b, a)
  const angle = Math.atan2(matrix.b, matrix.a);
  return angle * (180 / Math.PI);
}

/**
 * Get the cumulative transform matrix for an SVG element,
 * including all parent transforms up to the SVG root.
 */
function getCumulativeTransform(element: SVGGraphicsElement): DOMMatrix | null {
  try {
    // getScreenCTM() gives us the cumulative transformation matrix
    // from the element's coordinate system to screen coordinates
    const ctm = element.getScreenCTM();
    return ctm;
  } catch {
    return null;
  }
}

/**
 * Get element bounds from DOM using the bl_edit_selector class.
 * Returns bounds in screen coordinates.
 */
export function getElementByClassName(
  className: string,
  videoContainer: HTMLElement
): HTMLElement | SVGGraphicsElement | null {
  if (!className) return null;

  // Find element by class - escape special characters in the class name
  const escapedClassName = escapeCssSelector(className);
  const element = videoContainer.querySelector(`.${escapedClassName}`);
  return (element as HTMLElement | SVGGraphicsElement | null) || null;
}

export function getElementBoundsFromElement(
  element: HTMLElement | SVGGraphicsElement,
  videoContainer: HTMLElement
): ElementBounds | null {
  if (!element) return null;

  // Check if it's an SVG element
  const isSvgElement = element instanceof SVGGraphicsElement;
  if (isSvgElement) {
    return getSvgElementBounds(element as SVGGraphicsElement, videoContainer);
  }

  // Fallback for non-SVG elements
  return getHtmlElementBounds(element as HTMLElement, videoContainer);
}

export function getElementBoundsFromDOM(
  className: string,
  videoContainer: HTMLElement
): ElementBounds | null {
  const element = getElementByClassName(className, videoContainer);
  if (!element) return null;
  return getElementBoundsFromElement(element, videoContainer);
}

/**
 * Get bounds for an SVG element using getBBox and transform matrices.
 */
function getSvgElementBounds(
  element: SVGGraphicsElement,
  videoContainer: HTMLElement
): ElementBounds | null {
  try {
    // Get the bounding box in local coordinates
    const bbox = element.getBBox();

    // Get the cumulative transform matrix
    const ctm = getCumulativeTransform(element);
    if (!ctm) {
      return getHtmlElementBounds(
        (element as unknown) as HTMLElement,
        videoContainer
      );
    }

    // Get container position for relative coordinates
    const containerRect = videoContainer.getBoundingClientRect();

    // Transform the four corners of the bbox to screen coordinates
    const svg = element.ownerSVGElement;
    if (!svg) return null;

    const svgPoint = svg.createSVGPoint();

    // Helper to transform a point
    const transformPoint = (x: number, y: number): Point => {
      svgPoint.x = x;
      svgPoint.y = y;
      const screenPoint = svgPoint.matrixTransform(ctm);
      return {
        x: screenPoint.x - containerRect.left,
        y: screenPoint.y - containerRect.top,
      };
    };

    // Get the four corners
    const nw = transformPoint(bbox.x, bbox.y);
    const ne = transformPoint(bbox.x + bbox.width, bbox.y);
    const se = transformPoint(bbox.x + bbox.width, bbox.y + bbox.height);
    const sw = transformPoint(bbox.x, bbox.y + bbox.height);

    // Calculate center
    const center = transformPoint(
      bbox.x + bbox.width / 2,
      bbox.y + bbox.height / 2
    );

    // Calculate the axis-aligned bounding box of the transformed corners
    const allX = [nw.x, ne.x, se.x, sw.x];
    const allY = [nw.y, ne.y, se.y, sw.y];
    const minX = Math.min(...allX);
    const minY = Math.min(...allY);

    // Extract rotation from the transform matrix
    const rotation = extractRotationFromMatrix(ctm);

    // Calculate the original (unrotated) width/height
    // by measuring the distance between corners
    const width = Math.sqrt(
      Math.pow(ne.x - nw.x, 2) + Math.pow(ne.y - nw.y, 2)
    );
    const height = Math.sqrt(
      Math.pow(sw.x - nw.x, 2) + Math.pow(sw.y - nw.y, 2)
    );

    return {
      x: minX,
      y: minY,
      width,
      height,
      rotation,
      centerX: center.x,
      centerY: center.y,
      corners: { nw, ne, se, sw },
    };
  } catch (error) {
    console.warn("[elementBoundsTracker] Error getting SVG bounds:", error);
    return null;
  }
}

/**
 * Get bounds for an HTML element using getBoundingClientRect.
 */
function getHtmlElementBounds(
  element: HTMLElement,
  videoContainer: HTMLElement
): ElementBounds | null {
  try {
    const rect = element.getBoundingClientRect();
    const containerRect = videoContainer.getBoundingClientRect();

    // Get relative position
    const x = rect.left - containerRect.left;
    const y = rect.top - containerRect.top;
    const width = rect.width;
    const height = rect.height;

    // Try to extract rotation from computed style
    let rotation = 0;
    const style = window.getComputedStyle(element);
    const transform = style.transform;
    if (transform && transform !== "none") {
      const matrix = new DOMMatrix(transform);
      rotation = extractRotationFromMatrix(matrix);
    }

    const centerX = x + width / 2;
    const centerY = y + height / 2;

    return {
      x,
      y,
      width,
      height,
      rotation,
      centerX,
      centerY,
      corners: {
        nw: { x, y },
        ne: { x: x + width, y },
        se: { x: x + width, y: y + height },
        sw: { x, y: y + height },
      },
    };
  } catch (error) {
    console.warn("[elementBoundsTracker] Error getting HTML bounds:", error);
    return null;
  }
}

/**
 * Find an element by searching for multiple possible class name formats.
 */
export function findElementByLayerInfo(
  videoContainer: HTMLElement,
  layerUid?: number,
  layerName?: string,
  assetName?: string
): HTMLElement | SVGElement | null {
  // Try by UID first (most reliable)
  // Use attribute selector with contains (*=) which doesn't need escaping for the value
  if (typeof layerUid === "number") {
    const uidToken = `$l_uid_${layerUid}`;
    const elements = videoContainer.querySelectorAll(
      `[class*="bl_edit_selector"][class*="${uidToken}"]`
    );
    if (elements.length > 0) {
      return elements[0] as HTMLElement | SVGElement;
    }
  }

  // Try by layer name - escape special characters
  if (layerName) {
    const escapedLayerName = escapeCssSelector(layerName);
    const escapedAssetName = assetName ? escapeCssSelector(assetName) : null;
    const selector = escapedAssetName
      ? `.bl_edit_selector-\\$a_${escapedAssetName}_\\$l_${escapedLayerName}`
      : `.bl_edit_selector-\\$l_${escapedLayerName}`;
    const element = videoContainer.querySelector(selector);
    if (element) {
      return element as HTMLElement | SVGElement;
    }
  }

  return null;
}

/**
 * Get the video container element.
 */
export function getVideoContainer(): HTMLElement | null {
  return document.getElementById("vid-container");
}
